import React from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useTranslation } from 'react-i18next';
import { PassOfferSummary } from '../../models/Pass';
import type { TutorSessionType } from '../../models/Tutor';
import PassOfferCard from './PassOfferCard';
import {
  resolvePassOfferSessionType,
  resolvePassOfferTargetNames,
} from '../../utils/passFormatting';

interface PassOfferListProps {
  offers: PassOfferSummary[];
  onBuy: (offer: PassOfferSummary) => void;
  loading?: boolean;
  title?: string;
  /**
   * Optional fallback when an offer has no embedded `targets`
   * (e.g. host list, or page-local room names).
   */
  sessionTypeByTargetId?: Record<string, TutorSessionType | null | undefined>;
  targetNameByTargetId?: Record<string, string | null | undefined>;
}

/** Reusable list of buyable pass offers, shown near spaces/tutors/lesson options. Renders nothing when empty. */
const PassOfferList: React.FC<PassOfferListProps> = ({
  offers,
  onBuy,
  loading = false,
  title,
  sessionTypeByTargetId,
  targetNameByTargetId,
}) => {
  const { t } = useTranslation();

  if (!loading && offers.length === 0) {
    return null;
  }

  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        <ConfirmationNumberIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="overline" color="text.secondary">
          {title ?? t('passes.offersTitle', 'Pass offers')}
        </Typography>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Stack spacing={1}>
          {offers.map((offer) => (
            <PassOfferCard
              key={offer.id}
              offer={offer}
              sessionType={resolvePassOfferSessionType(offer, sessionTypeByTargetId)}
              targetNames={resolvePassOfferTargetNames(offer, targetNameByTargetId)}
              onBuy={onBuy}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PassOfferList;
